import React, {useCallback, useRef, useState} from 'react'
import {IMenuModel, IMenuReorderRequest} from './MenuModel'

interface MenuTreeProps {
  tree: IMenuModel[]
  onEdit: (item: IMenuModel) => void
  onDelete: (item: IMenuModel) => void
  onAddChild: (parentItem: IMenuModel) => void
  onReorder: (reorderItems: IMenuReorderRequest[]) => void
}

interface DragState {
  draggedId: string | null
  overId: string | null
  dropPosition: 'before' | 'after' | 'inside' | null
}

const MenuTree: React.FC<MenuTreeProps> = ({tree, onEdit, onDelete, onAddChild, onReorder}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [dragState, setDragState] = useState<DragState>({
    draggedId: null,
    overId: null,
    dropPosition: null,
  })
  const dragCounterRef = useRef(0)

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getChildren = (node: IMenuModel): IMenuModel[] => node.children ?? []

  const getAllChildIds = (node: IMenuModel): string[] => {
    const ids: string[] = []
    getChildren(node).forEach((child) => {
      ids.push(child.id!)
      ids.push(...getAllChildIds(child))
    })
    return ids
  }

  const removeNode = (nodes: IMenuModel[], id: string): IMenuModel[] => {
    return nodes
      .filter((n) => n.id !== id)
      .map((n) => ({...n, children: removeNode(getChildren(n), id)}))
  }

  const findNode = (nodes: IMenuModel[], id: string): IMenuModel | null => {
    for (const node of nodes) {
      if (node.id === id) return node
      const found = findNode(getChildren(node), id)
      if (found) return found
    }
    return null
  }

  const insertNode = (
    nodes: IMenuModel[],
    targetId: string,
    movedNode: IMenuModel,
    position: 'before' | 'after' | 'inside'
  ): IMenuModel[] => {
    if (position === 'inside') {
      return nodes.map((n) => {
        if (n.id === targetId) {
          return {...n, children: [...getChildren(n), {...movedNode, parentId: n.id!}]}
        }
        return {...n, children: insertNode(getChildren(n), targetId, movedNode, position)}
      })
    }

    const result: IMenuModel[] = []
    for (const node of nodes) {
      if (node.id === targetId) {
        if (position === 'before') {
          result.push({...movedNode, parentId: node.parentId})
          result.push(node)
        } else {
          result.push(node)
          result.push({...movedNode, parentId: node.parentId})
        }
      } else {
        result.push({
          ...node,
          children: insertNode(getChildren(node), targetId, movedNode, position),
        })
      }
    }
    return result
  }

  const assignOrderIndices = (
    nodes: IMenuModel[],
    parentId: string | null = null
  ): IMenuReorderRequest[] => {
    const result: IMenuReorderRequest[] = []
    nodes.forEach((node, index) => {
      result.push({id: node.id!, parentId, orderIndex: index})
      if (getChildren(node).length > 0) {
        result.push(...assignOrderIndices(getChildren(node), node.id!))
      }
    })
    return result
  }

  const handleDragStart = (e: React.DragEvent, item: IMenuModel) => {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', item.id!)
    setDragState({draggedId: item.id!, overId: null, dropPosition: null})
  }

  const handleDragOver = (e: React.DragEvent, item: IMenuModel) => {
    e.preventDefault()
    e.stopPropagation()

    if (!dragState.draggedId || dragState.draggedId === item.id) return

    const draggedNode = findNode(tree, dragState.draggedId)
    if (draggedNode) {
      const childIds = getAllChildIds(draggedNode)
      if (childIds.includes(item.id!)) return
    }

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const y = e.clientY - rect.top
    const height = rect.height

    let position: 'before' | 'after' | 'inside'
    if (y < height * 0.25) {
      position = 'before'
    } else if (y > height * 0.75) {
      position = 'after'
    } else {
      position = 'inside'
    }

    setDragState((prev) => ({...prev, overId: item.id!, dropPosition: position}))
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current++
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setDragState((prev) => ({...prev, overId: null, dropPosition: null}))
    }
  }

  const handleDrop = (e: React.DragEvent, targetItem: IMenuModel) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0

    const {draggedId, dropPosition} = dragState
    if (!draggedId || !dropPosition || draggedId === targetItem.id) {
      setDragState({draggedId: null, overId: null, dropPosition: null})
      return
    }

    const draggedNode = findNode(tree, draggedId)
    if (!draggedNode) {
      setDragState({draggedId: null, overId: null, dropPosition: null})
      return
    }

    const childIds = getAllChildIds(draggedNode)
    if (childIds.includes(targetItem.id!)) {
      setDragState({draggedId: null, overId: null, dropPosition: null})
      return
    }

    let newTree = removeNode(tree, draggedId)
    newTree = insertNode(newTree, targetItem.id!, draggedNode, dropPosition)
    const reorderItems = assignOrderIndices(newTree)

    if (dropPosition === 'inside') {
      setExpandedIds((prev) => {
        const next = new Set(Array.from(prev))
        next.add(targetItem.id!)
        return next
      })
    }

    onReorder(reorderItems)
    setDragState({draggedId: null, overId: null, dropPosition: null})
  }

  const handleDragEnd = () => {
    dragCounterRef.current = 0
    setDragState({draggedId: null, overId: null, dropPosition: null})
  }

  const getDropIndicatorStyle = (
    itemId: string
  ): {borderTop?: string; borderBottom?: string; backgroundColor?: string} => {
    if (dragState.overId !== itemId || !dragState.dropPosition) return {}

    switch (dragState.dropPosition) {
      case 'before':
        return {borderTop: '2px solid #009ef7'}
      case 'after':
        return {borderBottom: '2px solid #009ef7'}
      case 'inside':
        return {backgroundColor: 'rgba(0, 158, 247, 0.08)'}
      default:
        return {}
    }
  }

  const renderTreeNode = (node: IMenuModel, depth: number) => {
    const isExpanded = expandedIds.has(node.id!)
    const children = getChildren(node)
    const hasChildren = children.length > 0
    const isDragged = dragState.draggedId === node.id

    return (
      <div key={node.id} className={isDragged ? 'opacity-50' : ''}>
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, node)}
          onDragOver={(e) => handleDragOver(e, node)}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node)}
          onDragEnd={handleDragEnd}
          className='d-flex align-items-center py-2 px-3 rounded cursor-pointer menu-tree-item'
          style={{
            marginLeft: `${depth * 24}px`,
            transition: 'background-color 0.15s',
            userSelect: 'none',
            ...getDropIndicatorStyle(node.id!),
          }}
        >
          <span
            className='me-2'
            style={{width: '20px', cursor: hasChildren ? 'pointer' : 'default'}}
            onClick={() => hasChildren && toggleExpand(node.id!)}
          >
            {hasChildren && (
              <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} fs-7`} />
            )}
          </span>

          <span className='me-2 text-gray-400 cursor-grab' style={{cursor: 'grab'}}>
            <i className='bi bi-grip-vertical' />
          </span>

          {node.icon && (
            <span className='me-2'>
              <i className={`bi ${node.icon} fs-5 text-primary`} />
            </span>
          )}

          <span className='flex-grow-1 fw-semibold text-gray-800'>{node.title}</span>

          <span className='text-muted fs-7 me-4'>{node.path}</span>

          <span className={`badge ${node.isActive ? 'badge-light-success' : 'badge-light-danger'} me-3`}>
            {node.isActive ? 'Aktif' : 'Pasif'}
          </span>

          <div className='d-flex gap-1'>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-primary'
              title='Alt Menü Ekle'
              onClick={(e) => {
                e.stopPropagation()
                onAddChild(node)
              }}
            >
              <i className='bi bi-plus-lg fs-7' />
            </button>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-warning'
              title='Düzenle'
              onClick={(e) => {
                e.stopPropagation()
                onEdit(node)
              }}
            >
              <i className='bi bi-pencil fs-7' />
            </button>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-danger'
              title='Sil'
              onClick={(e) => {
                e.stopPropagation()
                onDelete(node)
              }}
            >
              <i className='bi bi-trash fs-7' />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>{children.map((child) => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    )
  }

  return (
    <div className='menu-tree'>
      {tree.length === 0 ? (
        <div className='text-center text-muted py-10'>
          <i className='bi bi-list-nested fs-1 d-block mb-3' />
          <p>Henüz menü eklenmemiş.</p>
        </div>
      ) : (
        tree.map((node) => renderTreeNode(node, 0))
      )}
    </div>
  )
}

export default MenuTree
